import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, XCircle, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { API } from "../lib/utils";

const PaymentSuccessPage = () => {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: null, attempts: 0 });
  const pollingRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      navigate("/");
      return;
    }
    if (pollingRef.current) return;
    pollingRef.current = true;

    const poll = async (attempts = 0) => {
      if (attempts >= 8) {
        setState({ status: "timeout", payment_status: null, attempts });
        return;
      }
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState({ status: "paid", payment_status: "paid", attempts });
          return;
        }
        if (res.data.status === "expired") {
          setState({ status: "expired", payment_status: res.data.payment_status, attempts });
          return;
        }
        setTimeout(() => poll(attempts + 1), 2000);
      } catch (e) {
        setTimeout(() => poll(attempts + 1), 2000);
      }
    };
    poll();
  }, [sessionId, navigate]);

  let icon, title, body, iconColor;
  if (state.status === "paid") {
    icon = <CheckCircle2 size={64} />;
    iconColor = "text-burnt";
    title = t("payment.success_title");
    body = t("payment.success_body");
  } else if (state.status === "expired") {
    icon = <XCircle size={64} />;
    iconColor = "text-destructive";
    title = t("payment.expired_title");
    body = t("payment.expired_body");
  } else if (state.status === "timeout") {
    icon = <Clock size={64} />;
    iconColor = "text-burnt";
    title = t("payment.pending_title");
    body = t("payment.pending_body");
  } else {
    icon = <Clock size={64} className="animate-pulse" />;
    iconColor = "text-burnt";
    title = t("payment.verifying");
    body = "";
  }

  return (
    <div data-testid="page-payment-success" className="bg-white min-h-[70vh] flex items-center">
      <div className="container-flow text-center max-w-2xl mx-auto py-20">
        <div className={`inline-flex ${iconColor} mb-8`}>{icon}</div>
        <h1 className="hd-italic text-5xl md:text-6xl text-forest" data-testid="payment-status-title">{title}</h1>
        {body && <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{body}</p>}
        <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/" className="btn-secondary" data-testid="payment-back-home">
            {t("payment.back_home")} <ArrowRight size={18} />
          </Link>
          {state.status === "expired" && (
            <Link to="/reservation" className="btn-primary">
              {t("nav.book_cta")} <ArrowRight size={18} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
